import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import './css/StepGuideModal.css';

/**
 * StepGuideModal - A modal component that displays step-by-step guides
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {string} props.title - Title of the guide
 * @param {string} props.description - Main description text
 * @param {Array<string>} props.bullets - Array of bullet points explaining the step
 * @param {string} [props.note] - Optional note text displayed beneath the description
 */
const StepGuideModal = ({
  isOpen,
  onClose,
  title,
  description,
  bullets = [],
  note = null
}) => {
  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Safety check for SSR
  if (typeof document === 'undefined') return null;

  // Render modal using React Portal to document.body to avoid stacking context issues
  // Use very high z-index (99999) with CSS !important to ensure it's above all other elements
  // including sticky headers and table headers
  return ReactDOM.createPortal(
    <div
      className="step-guide-modal-overlay flex items-center justify-center p-4"
      onClick={(e) => {
        // Close on backdrop click
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="step-guide-title"
    >
      {/* Backdrop */}
      <div className="step-guide-modal-backdrop inset-0 bg-black bg-opacity-50 transition-opacity" />

      {/* Modal */}
      <div className="step-guide-modal-content bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
              <FaInfoCircle className="text-white text-lg" />
            </div>
            <h2
              id="step-guide-title"
              className="text-2xl font-bold text-gray-900 font-ui"
            >
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close guide"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Description */}
          {description && (
            <p
              className={`text-gray-700 text-base leading-relaxed ${note ? 'mb-2' : 'mb-6'} font-ui`}
            >
              {description}
            </p>
          )}

          {note && (
            <p className="text-gray-500 text-sm leading-relaxed italic mb-6 font-ui">
              {note}
            </p>
          )}

          {/* Bullet Points */}
          {bullets && bullets.length > 0 && (
            <div className="space-y-3 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 font-ui">
                What this step contributes:
              </h3>
              <ul className="space-y-3">
                {bullets.map((bullet, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <FaCheckCircle className="text-blue-600 text-lg" />
                    </div>
                    <span className="text-gray-700 text-base leading-relaxed font-ui">
                      {bullet}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-ui"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StepGuideModal;

